import * as React from 'react'
import * as T from 'components/ui/typography'
import * as firebaseSdk from 'sdk/firebase'
import * as AuthUtils from 'utils/auth/auth'
import * as TestySDK from 'sdk/testy-web-sdk'

import { useRouter } from 'next/router'

import Button from 'components/shared/button'
import FormTextField from 'components/shared/form-field-input'
import AppLogo from 'components/shared/logo'

import Routes from 'utils/routes'
import { AuthContext } from 'providers/auth-provider'
import { useSnackbar } from 'notistack'
import useTranslation from 'next-translate/useTranslation'
import { useFormik } from 'formik'
import { ConfirmPasswordSchema } from 'utils/form/validation-schema'
import { analytics } from 'utils/analytics'
import IconButton from 'components/shared/icon-button'
import { Eye, EyeOff } from 'lucide-react'

export default function VerifyPassword({ email, name, phone }: { email: string; name: string; phone?: string }) {
    const router = useRouter()
    const { t } = useTranslation('common')

    const [loading, setLoading] = React.useState(false)
    const { setUser } = React.useContext(AuthContext)
    const snackbar = useSnackbar()

    const [passwordHelperText, setPasswordHelperText] = React.useState('')
    const [showPassword, setShowPassword] = React.useState(false)

    const [passwordRepeatHelperText, setPasswordRepeatHelperText] = React.useState('')
    const [showPasswordRepeat, setShowPasswordRepeat] = React.useState(false)

    const form = useFormik({
        initialValues: {
            password: '',
            confirmPassword: '',
        },
        onSubmit: () => {
            onSubmit()
        },
        validationSchema: ConfirmPasswordSchema,
    })

    const signUpWithCredentials = async () => {
        try {
            const user = await firebaseSdk.signUpWithEmailAndPassword(email, form.values.password)
            const idToken = await user.getIdToken(true)
            const token = await AuthUtils.exchangeFirebaseTokenWithCustom(idToken)
            const userBasicData = AuthUtils.userBasicDataParser(user, token)
            TestySDK.setAuthToken(userBasicData.token)

            if (token) {
                if (phone !== '')
                    await TestySDK.createUser({
                        email: user.email,
                        name: name,
                        phone_number: phone,
                    })
                else
                    await TestySDK.createUser({
                        email: user.email,
                        name: name,
                    })
                analytics.track('sign_up_success')
                setUser(userBasicData)
                analytics.identify(`${userBasicData.email}`, userBasicData)
                TestySDK.setAuthToken(userBasicData.token)
                router.push(Routes.Onboarding)
            }
        } catch (e) {
            analytics.track('sign_up_error', { value: e })
            snackbar.enqueueSnackbar(e, { variant: 'error' })
            setLoading(false)
        }
    }

    const onSubmit = async () => {
        setPasswordHelperText('')
        setPasswordRepeatHelperText('')

        setLoading(true)
        await signUpWithCredentials()
    }

    const handleErrorPassword = React.useMemo(() => {
        if (form.errors.password) return t(`${form.errors.password}`)
        if (passwordHelperText) return passwordHelperText
        else return ''
    }, [form.errors.password, passwordHelperText, t])

    const handleErrorConfirmPassword = React.useMemo(() => {
        if (form.errors.confirmPassword) return t(`${form.errors.confirmPassword}`)
        if (passwordRepeatHelperText) return passwordRepeatHelperText
        else return ''
    }, [form.errors.confirmPassword, passwordRepeatHelperText, t])

    return (
        <div>
            <AppLogo />

            <T.H2 className="mt-3 mb-2 text-start">{t('confirmPassTitle')}</T.H2>

            <T.H3 className="font-light mb-6 text-lg text-start">{t('confirmPassDesc')}</T.H3>

            <FormTextField
                value={form.values.password}
                error={handleErrorPassword}
                onChangeFn={form.handleChange('password')}
                data-testid="password"
                placeholder={t('insertPass')}
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                    <IconButton className="rounded-full mx-0" onClick={() => setShowPassword((show) => !show)}>
                        {!showPassword ? (
                            <Eye className="h-5 w-5 text-neutral-600" />
                        ) : (
                            <EyeOff className="h-5 w-5 text-neutral-600" />
                        )}
                    </IconButton>
                }
                password
            />
            <FormTextField
                value={form.values.confirmPassword}
                error={handleErrorConfirmPassword}
                data-testid="confirm-password"
                onChangeFn={form.handleChange('confirmPassword')}
                placeholder={t('confirmPass')}
                type={showPasswordRepeat ? 'text' : 'password'}
                endAdornment={
                    <IconButton className="rounded-full mx-0" onClick={() => setShowPasswordRepeat((show) => !show)}>
                        {!showPasswordRepeat ? (
                            <Eye className="h-5 w-5 text-neutral-600" />
                        ) : (
                            <EyeOff className="h-5 w-5 text-neutral-600" />
                        )}
                    </IconButton>
                }
                password
            />

            <Button
                className="w-full mt-2"
                data-testid="signup-button"
                disabled={!form.isValid}
                loading={loading}
                onClick={onSubmit}>
                Continua
            </Button>
        </div>
    )
}
